function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { Link } from "react-router-dom";
import { repSvc, safe, errText, rangeLabel, apiRoot, authHeaders } from "./common";
import account from "../../data/account";
import RangePicker from "../../components/range-picker";
import { isCustomRange } from "../sales/territory-common";

/*
 * "Email this report" side sheet (REPORT_SCHEDULES_UX.md §2 ②; reportsflow
 * board 6, 2026-10-09). Opened from a Reports list row, the Emails tab, the
 * viewer, or the sales executive view. The person picks local time in a
 * chosen zone; STORAGE stays hourUtc (+ the UTC day) so the worker's due
 * math is untouched. Recipient chips with validation (max 10), what each
 * email covers, compare toggle, sending on/paused, "Send a test to me",
 * the real email preview and recent sends. Styles: sass/v2/_reports-list.scss (rl-).
 *
 * API: PUT/DELETE /v1/reports/{id}/schedule · POST …/schedule/send-now
 * {test} · GET …/deliveries?limit=5 · GET …/email (preview).
 */

export const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
// What each email covers: plain wording for the rolling windows. Any other
// saved token (an older schedule) is appended with its generic label.
export const COVERS = [["last_7_days", "The 7 days before it is sent"], ["last_30_days", "The 30 days before it is sent"], ["last_month", "The month before"], ["this_month", "This month so far"], ["", "The report's own dates"]];
export function coversHint(range) {
  switch (range || "") {
    case "last_7_days":
      return "covers the 7 days before";
    case "last_30_days":
      return "covers the 30 days before";
    case "last_month":
      return "covers the month before";
    case "this_month":
      return "covers this month so far";
    case "":
      return "uses the report's own dates";
    default:
      return isCustomRange(range) ? `covers ${rangeLabel(range)}` : `covers ${rangeLabel(range).toLowerCase()}`;
  }
}
// Sending zones: US first (the tenants), then the common international set;
// the browser's zone is added if it isn't listed. Storage stays UTC.
export const TIMEZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu", "America/Toronto", "America/Vancouver", "America/Mexico_City", "America/Sao_Paulo", "UTC", "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Madrid", "Europe/Rome", "Europe/Amsterdam", "Europe/Stockholm", "Europe/Warsaw", "Europe/Istanbul", "Asia/Dubai", "Asia/Kolkata", "Asia/Bangkok", "Asia/Singapore", "Asia/Hong_Kong", "Asia/Shanghai", "Asia/Tokyo", "Asia/Seoul", "Australia/Sydney", "Australia/Perth", "Pacific/Auckland"];
/** "America/New_York" → "New York time"; the zero-offset zone reads "Universal time". */
export function tzCity(tz) {
  if (!tz || tz === "UTC" || tz === "Etc/UTC") return "Universal time";
  const city = String(tz).split("/").pop().replace(/_/g, " ");
  return `${city} time`;
}
export function tzOptions(current) {
  const list = TIMEZONES.includes(current) ? TIMEZONES : [current, ...TIMEZONES];
  return list.map(z => [z, tzCity(z)]);
}
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function browserTz() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (e) {
    return "UTC";
  }
}
// Minutes to ADD to UTC to get wall time in `tz` (ET in September = -240).
export function tzOffsetMinutes(tz, date = new Date()) {
  try {
    const f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
    // Compare at minute precision: formatToParts drops seconds, so
    // subtracting a raw timestamp leaves a sub-minute residue that rounds
    // the offset to -239 and turns hourUtc into 13.0166… (seen 09-11).
    const at = new Date(date);
    at.setUTCSeconds(0, 0);
    const p = {};
    f.formatToParts(at).forEach(x => {
      if (x.type !== "literal") p[x.type] = x.value;
    });
    const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute);
    return Math.round((asUtc - at.getTime()) / 60000);
  } catch (e) {
    return 0;
  }
}
function shift(cadence, day, hoursTotal) {
  hoursTotal = Math.round(hoursTotal); // whole hours only: offsets like +5:30 round to the nearest hour
  const dayShift = Math.floor(hoursTotal / 24);
  const hour = (hoursTotal % 24 + 24) % 24;
  const d = cadence === "weekly" ? ((day + dayShift) % 7 + 7) % 7 : Math.min(28, Math.max(1, day + dayShift));
  return {
    day: d,
    hour
  };
}
export function localToUtc(cadence, dayLocal, hourLocal, tz) {
  const r = shift(cadence, Number(dayLocal), Number(hourLocal) - tzOffsetMinutes(tz) / 60);
  return {
    dayUtc: r.day,
    hourUtc: r.hour
  };
}
export function utcToLocal(cadence, dayUtc, hourUtc, tz) {
  const r = shift(cadence, Number(dayUtc), Number(hourUtc) + tzOffsetMinutes(tz) / 60);
  return {
    dayLocal: r.day,
    hourLocal: r.hour
  };
}
// Dates from the API are UTC without a zone suffix (Dapper DateTime): pin them.
export function asUtcDate(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  const s = String(v);
  return new Date(/[zZ]|[+-]\d{2}:\d{2}$/.test(s) ? s : s + "Z");
}
export function fmtLocal(v, tz, withTz) {
  const d = asUtcDate(v);
  if (!d || isNaN(d.getTime())) return "";
  const txt = d.toLocaleString("en-US", {
    timeZone: tz || browserTz(),
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
  return withTz ? `${txt} ${tzAbbrev(tz)}` : txt;
}
/** "Wed, Oct 7 at 10:00 AM" (long: "Wednesday, Oct 14 at 10:00 AM"). */
export function fmtWhen(v, tz, long) {
  const d = asUtcDate(v);
  if (!d || isNaN(d.getTime())) return "";
  const zone = tz || browserTz();
  const day = d.toLocaleDateString("en-US", {
    timeZone: zone,
    weekday: long ? "long" : "short",
    month: "short",
    day: "numeric"
  });
  const time = d.toLocaleTimeString("en-US", {
    timeZone: zone,
    hour: "numeric",
    minute: "2-digit"
  });
  return `${day} at ${time}`;
}
/** "Wed, Oct 14" in the browser's zone. */
export function fmtDayShort(v) {
  const d = asUtcDate(v);
  if (!d || isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric"
  });
}
/** "Oct 2" (with the year when it isn't this year), browser zone. */
export function fmtShortDate(v) {
  const d = asUtcDate(v);
  if (!d || isNaN(d.getTime())) return "";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric"
  });
}
export function tzAbbrev(tz) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz || browserTz(),
      timeZoneName: "short"
    }).formatToParts(new Date());
    return (parts.find(p => p.type === "timeZoneName") || {}).value || "";
  } catch (e) {
    return "";
  }
}
// Client mirror of the server's NextDueUtc (schedule with UTC day/hour).
export function nextDueUtc(s) {
  if (!s || !s.cadence || s.paused) return null;
  const now = new Date();
  const hour = Number(s.hourUtc) || 0;
  const day = Number(s.day) || 0;
  let recent;
  let step;
  if (s.cadence === "weekly") {
    const delta = ((now.getUTCDay() - day) % 7 + 7) % 7;
    recent = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - delta, hour));
    if (recent > now) recent = new Date(recent.getTime() - 7 * 86400000);
    step = d => new Date(d.getTime() + 7 * 86400000);
  } else if (s.cadence === "monthly") {
    const t = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), Math.min(day, 28), hour));
    recent = t <= now ? t : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, Math.min(day, 28), hour));
    step = d => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), d.getUTCHours()));
  } else return null;
  // A schedule with no send history counts the most recent past occurrence
  // as done (the server stamps lastSentDate=now on first save): "first
  // email" is the NEXT occurrence, never a catch-up send.
  const ls = s.lastSentDate ? asUtcDate(s.lastSentDate) : now;
  return ls && ls >= recent ? step(recent) : recent;
}
export function fmtHour(h) {
  const n = Number(h);
  return `${(n + 11) % 12 + 1}:00 ${n < 12 ? "AM" : "PM"}`;
}
/** "10 AM" (short form for the list's chip line). */
export function fmtHourShort(h) {
  const n = Number(h);
  return `${(n + 11) % 12 + 1} ${n < 12 ? "AM" : "PM"}`;
}

/**
 * Plain wording for a schedule, in the schedule's own zone.
 * parts: { day: "Every Wednesday" | "Day 1 of each month", hour: local hour, tz, away: true when that zone isn't the viewer's }
 */
export function scheduleParts(s, viewerTz) {
  if (!s || !s.cadence) return null;
  const tz = s.timezone || viewerTz || browserTz();
  const loc = utcToLocal(s.cadence, s.day, s.hourUtc, tz);
  const day = s.cadence === "weekly" ? `Every ${DAYS[loc.dayLocal]}` : `Day ${loc.dayLocal} of each month`;
  return {
    day,
    hour: loc.hourLocal,
    tz,
    away: tz !== (viewerTz || browserTz())
  };
}
/** "Every Wednesday at 10:00 AM" (+ " New York time" when the zone differs from the viewer's). */
export function whenLabel(s, viewerTz) {
  const p = scheduleParts(s, viewerTz);
  if (!p) return "";
  return `${p.day} at ${fmtHour(p.hour)}${p.away ? ` ${tzCity(p.tz)}` : ""}`;
}
// Kept for older callers: the plain sentence above, shortened.
export function cadenceLabel(s, tz) {
  return whenLabel(s, tz);
}

/**
 * Queue a test copy to the signed-in person. Raw fetch (like exportCsv): the
 * service layer hid the 4xx body, and the server needs the signed-in email
 * when the token carries none. Resolves to the address it went to.
 */
export async function sendTestEmail(reportId) {
  const me = account.getEmail && account.getEmail() || "";
  const res = await fetch(`${apiRoot()}/reports/${reportId}/schedule/send-now`, {
    method: "POST",
    headers: {
      ...authHeaders(),
      "content-type": "application/json"
    },
    body: JSON.stringify({
      test: true,
      email: me || null
    })
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `The test didn't go out (${res.status}).`);
  return me;
}
const people = n => `${n} ${n === 1 ? "person" : "people"}`;
export default class ScheduleSheet extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onKey", e => {
      if (e.key === "Escape") this.props.onClose();
    });
    _defineProperty(this, "save", async () => {
      if (!this.addDraft()) return null;
      const body = this.body();
      if (!body.recipients.length) {
        this.setState({
          note: "Add at least one person to send it to.",
          noteKind: "error"
        });
        return null;
      }
      this.setState({
        busy: true,
        note: null
      });
      try {
        const res = await repSvc(`/${this.id()}/schedule`).putAsync(body);
        if (!this._alive) return null;
        const next = res && res.nextDueUtc ? asUtcDate(res.nextDueUtc) : nextDueUtc({
          ...body,
          lastSentDate: this.state.lastSentDate
        });
        this.setState({
          busy: false,
          dirty: false,
          exists: true,
          note: body.paused ? "Saved. Sending is paused, so nothing goes out until you turn it back on." : `Saved. The first email goes out ${fmtWhen(next, this.state.tz, true)}.`,
          noteKind: "info"
        });
        if (this.props.onChanged) this.props.onChanged();
        return res || {
          ok: true
        };
      } catch (e) {
        if (this._alive) this.setState({
          busy: false,
          note: errText(e, "That didn't save. Try again."),
          noteKind: "error"
        });
        return null;
      }
    });
    _defineProperty(this, "sendTest", async () => {
      if (this.state.dirty || !this.state.exists) {
        const ok = await this.save();
        if (!ok) return;
      }
      this.setState({
        busy: true,
        note: null
      });
      try {
        const me = await sendTestEmail(this.id());
        if (this._alive) this.setState({
          busy: false,
          note: `A test is on its way to ${me || "you"}. It arrives within 5 minutes.`,
          noteKind: "info"
        });
        this.refreshDeliveries(60000);
      } catch (e) {
        if (this._alive) this.setState({
          busy: false,
          note: errText(e, "The test didn't go out."),
          noteKind: "error"
        });
      }
    });
    _defineProperty(this, "remove", async () => {
      this.setState({
        busy: true,
        note: null
      });
      try {
        await repSvc(`/${this.id()}/schedule`).deleteAsync();
        if (this.props.onChanged) this.props.onChanged();
        if (this._alive) this.props.onClose();
      } catch (e) {
        if (this._alive) this.setState({
          busy: false,
          confirmStop: false,
          note: errText(e, "Couldn't stop the emails. Try again."),
          noteKind: "error"
        });
      }
    });
    const s = props.schedule || parseSchedule(props.scheduleJson);
    const tz = s && s.timezone || browserTz();
    const loc = s ? utcToLocal(s.cadence, s.day, s.hourUtc, tz) : {
      dayLocal: 1,
      hourLocal: 9
    };
    this.state = {
      tz,
      exists: !!s,
      cadence: s && s.cadence || "weekly",
      day: loc.dayLocal,
      hour: loc.hourLocal,
      recipients: s && s.recipients || [],
      draft: "",
      range: s && s.range || "",
      comparison: s && s.comparison || "",
      paused: !!(s && s.paused),
      lastSentDate: s && s.lastSentDate,
      busy: false,
      note: null,
      noteKind: "info",
      deliveries: null,
      dirty: false,
      preview: null,
      previewBusy: false,
      confirmStop: false
    };
    this._alive = true;
  }
  async componentDidMount() {
    document.addEventListener("keydown", this.onKey);
    const d = await safe(repSvc(`/${this.id()}/deliveries?limit=5`).getAsync(), {
      items: []
    });
    if (this._alive) this.setState({
      deliveries: d.items || []
    });
    this.loadPreview();
  }
  // The real email: GET {id}/email is exactly what the worker sends (one
  // renderer, REPORT_SCHEDULES_UX.md §7). Re-rendered when the window changes.
  loadPreview() {
    clearTimeout(this._pvT);
    this._pvT = setTimeout(async () => {
      if (!this._alive) return;
      this.setState({
        previewBusy: true
      });
      const {
        range,
        comparison
      } = this.state;
      const qs = [];
      if (range) {
        const m = /^custom_(\d{4})(\d{2})(\d{2})_(\d{4})(\d{2})(\d{2})$/.exec(range);
        if (m) qs.push(`from=${m[1]}-${m[2]}-${m[3]}`, `to=${m[4]}-${m[5]}-${m[6]}`);else qs.push(`range=${encodeURIComponent(range)}`);
      }
      if (comparison) qs.push(`comparison=${encodeURIComponent(comparison)}`);
      const pv = await safe(repSvc(`/${this.id()}/email${qs.length ? "?" + qs.join("&") : ""}`).getAsync(), null);
      if (this._alive) this.setState({
        preview: pv,
        previewBusy: false
      });
    }, 350);
  }
  componentWillUnmount() {
    this._alive = false;
    clearTimeout(this._pvT);
    document.removeEventListener("keydown", this.onKey);
  }
  id() {
    return String(this.props.report.id);
  }
  set(patch) {
    this.setState({
      ...patch,
      dirty: true,
      note: null
    }, () => {
      if ("range" in patch || "comparison" in patch) this.loadPreview();
    });
  }
  body() {
    const {
      cadence,
      day,
      hour,
      tz,
      recipients,
      range,
      comparison,
      paused
    } = this.state;
    const u = localToUtc(cadence, day, hour, tz);
    return {
      cadence,
      day: u.dayUtc,
      hourUtc: u.hourUtc,
      recipients,
      paused,
      timezone: tz,
      range: range || null,
      comparison: comparison || null
    };
  }
  addDraft() {
    const parts = this.state.draft.split(/[\s,;]+/).map(x => x.trim()).filter(Boolean);
    if (!parts.length) return true;
    const bad = parts.filter(p => !EMAIL_RE.test(p));
    if (bad.length) {
      this.setState({
        note: `Use an email address for ${bad.join(", ")}.`,
        noteKind: "error"
      });
      return false;
    }
    const next = [...this.state.recipients];
    parts.forEach(p => {
      if (!next.some(r => r.toLowerCase() === p.toLowerCase())) next.push(p);
    });
    if (next.length > 10) {
      this.setState({
        note: "Up to 10 people can get each report.",
        noteKind: "error"
      });
      return false;
    }
    this.set({
      recipients: next,
      draft: ""
    });
    return true;
  }
  refreshDeliveries(delayMs) {
    setTimeout(async () => {
      if (!this._alive) return;
      const d = await safe(repSvc(`/${this.id()}/deliveries?limit=5`).getAsync(), null);
      if (this._alive && d) this.setState({
        deliveries: d.items || []
      });
    }, delayMs || 0);
  }
  renderWhen() {
    const {
      cadence,
      day,
      hour,
      tz,
      paused,
      lastSentDate
    } = this.state;
    const next = paused ? null : nextDueUtc({
      ...this.body(),
      lastSentDate
    });
    return /*#__PURE__*/React.createElement("div", {
      className: "rl-field"
    }, /*#__PURE__*/React.createElement("b", {
      className: "rl-field-h"
    }, "When"), /*#__PURE__*/React.createElement("div", {
      className: "rl-grid2"
    }, /*#__PURE__*/React.createElement("label", {
      className: "rl-lab"
    }, "How often", /*#__PURE__*/React.createElement("select", {
      className: "rl-select",
      value: cadence,
      onChange: e => this.set({
        cadence: e.target.value,
        day: 1
      })
    }, /*#__PURE__*/React.createElement("option", {
      value: "weekly"
    }, "Every week"), /*#__PURE__*/React.createElement("option", {
      value: "monthly"
    }, "Every month"))), /*#__PURE__*/React.createElement("label", {
      className: "rl-lab"
    }, "On", cadence === "weekly" ? /*#__PURE__*/React.createElement("select", {
      className: "rl-select",
      value: day,
      onChange: e => this.set({
        day: Number(e.target.value)
      })
    }, DAYS.map((d, i) => /*#__PURE__*/React.createElement("option", {
      key: i,
      value: i
    }, d))) : /*#__PURE__*/React.createElement("select", {
      className: "rl-select",
      value: day,
      onChange: e => this.set({
        day: Number(e.target.value)
      })
    }, Array.from({
      length: 28
    }, (_, i) => /*#__PURE__*/React.createElement("option", {
      key: i + 1,
      value: i + 1
    }, "Day ", i + 1)))), /*#__PURE__*/React.createElement("label", {
      className: "rl-lab"
    }, "At", /*#__PURE__*/React.createElement("select", {
      className: "rl-select",
      value: hour,
      onChange: e => this.set({
        hour: Number(e.target.value)
      })
    }, Array.from({
      length: 24
    }, (_, i) => /*#__PURE__*/React.createElement("option", {
      key: i,
      value: i
    }, fmtHour(i))))), /*#__PURE__*/React.createElement("label", {
      className: "rl-lab"
    }, "Time zone", /*#__PURE__*/React.createElement("select", {
      className: "rl-select",
      value: tz,
      onChange: e => this.set({
        tz: e.target.value
      })
    }, tzOptions(tz).map(([z, l]) => /*#__PURE__*/React.createElement("option", {
      key: z,
      value: z
    }, l))))), /*#__PURE__*/React.createElement("span", {
      className: "rl-hint"
    }, paused ? "Sending is paused, so no email is coming up." : next ? `Next email: ${fmtWhen(next, tz, true)}${tz !== browserTz() ? ` ${tzCity(tz)}` : ""}` : ""));
  }
  renderCovers() {
    const {
      range,
      comparison
    } = this.state;
    const known = COVERS.some(([v]) => v === range) || isCustomRange(range);
    const options = known ? COVERS : [...COVERS, [range, rangeLabel(range)]];
    const checked = comparison === "previous_period" || comparison === "previous_year";
    return /*#__PURE__*/React.createElement("div", {
      className: "rl-field"
    }, /*#__PURE__*/React.createElement("b", {
      className: "rl-field-h"
    }, "What each email covers"), /*#__PURE__*/React.createElement("div", {
      className: "rl-covers"
    }, /*#__PURE__*/React.createElement(RangePicker, {
      value: range,
      ranges: options,
      onChange: v => this.set({
        range: v || ""
      })
    })), /*#__PURE__*/React.createElement("label", {
      className: "rl-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: checked,
      onChange: e => this.set({
        comparison: e.target.checked ? "previous_period" : "none"
      })
    }), /*#__PURE__*/React.createElement("span", null, comparison === "previous_year" ? "Compare with the same time last year" : "Compare with the period before")), /*#__PURE__*/React.createElement("span", {
      className: "rl-hint"
    }, "Opening the report in Plunk still uses the date range you pick there."));
  }
  renderWho() {
    const {
      recipients,
      draft
    } = this.state;
    return /*#__PURE__*/React.createElement("div", {
      className: "rl-field"
    }, /*#__PURE__*/React.createElement("b", {
      className: "rl-field-h"
    }, "Who gets it"), /*#__PURE__*/React.createElement("div", {
      className: "rl-people",
      onClick: () => this._in && this._in.focus()
    }, recipients.map(r => /*#__PURE__*/React.createElement("span", {
      key: r,
      className: "rl-person"
    }, r, /*#__PURE__*/React.createElement("button", {
      type: "button",
      "aria-label": `Remove ${r}`,
      title: "Remove",
      onClick: e => {
        e.stopPropagation();
        this.set({
          recipients: recipients.filter(x => x !== r)
        });
      }
    }, "\xD7"))), /*#__PURE__*/React.createElement("input", {
      ref: el => {
        this._in = el;
      },
      value: draft,
      placeholder: "Add a name or email",
      "aria-label": "Add a name or email",
      onChange: e => this.setState({
        draft: e.target.value
      }),
      onKeyDown: e => {
        if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
          if (draft.trim()) {
            e.preventDefault();
            this.addDraft();
          }
        } else if (e.key === "Backspace" && !draft && recipients.length) {
          this.set({
            recipients: recipients.slice(0, -1)
          });
        }
      },
      onBlur: () => {
        if (draft.trim()) this.addDraft();
      }
    })), /*#__PURE__*/React.createElement("span", {
      className: "rl-hint"
    }, "Up to 10 people. People outside your team are fine. Replies go to you."));
  }
  renderSending() {
    const {
      paused,
      exists,
      confirmStop,
      busy
    } = this.state;
    return /*#__PURE__*/React.createElement("div", {
      className: "rl-sending"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      role: "switch",
      "aria-checked": !paused,
      className: "rl-switch" + (paused ? "" : " on"),
      "aria-label": paused ? "Turn sending on" : "Pause sending",
      onClick: () => this.set({
        paused: !paused
      })
    }, /*#__PURE__*/React.createElement("span", null)), /*#__PURE__*/React.createElement("div", {
      className: "rl-sending-t"
    }, /*#__PURE__*/React.createElement("b", null, paused ? "Sending is paused" : "Sending is on"), /*#__PURE__*/React.createElement("span", {
      className: "rl-hint"
    }, paused ? "Turn on to start again. Emails missed while paused are not sent later." : "Turn off to pause. Nothing is sent while paused.")), exists && (confirmStop ? /*#__PURE__*/React.createElement("span", {
      className: "rl-confirm"
    }, /*#__PURE__*/React.createElement("span", null, "Stop emailing?"), /*#__PURE__*/React.createElement("a", {
      className: "rl-danger",
      onClick: () => !busy && this.remove()
    }, "Yes, stop"), /*#__PURE__*/React.createElement("a", {
      onClick: () => this.setState({
        confirmStop: false
      })
    }, "Keep")) : /*#__PURE__*/React.createElement("a", {
      className: "rl-danger",
      onClick: () => this.setState({
        confirmStop: true
      })
    }, "Stop emailing")));
  }
  renderSide() {
    const {
      tz,
      preview,
      previewBusy,
      deliveries,
      lastSentDate
    } = this.state;
    const {
      onSeeAll
    } = this.props;
    return /*#__PURE__*/React.createElement("div", {
      className: "rl-sheet-side"
    }, /*#__PURE__*/React.createElement("div", {
      className: "rl-side-h"
    }, /*#__PURE__*/React.createElement("b", null, "What they will get"), previewBusy ? /*#__PURE__*/React.createElement("span", {
      className: "rl-hint"
    }, "Updating\u2026") : null), preview && preview.subject ? /*#__PURE__*/React.createElement("div", {
      className: "rl-subject"
    }, "Subject: ", preview.subject) : null, /*#__PURE__*/React.createElement("div", {
      className: "rl-preview"
    }, preview && preview.html ? /*#__PURE__*/React.createElement("iframe", {
      title: "Email preview",
      sandbox: "",
      srcDoc: `<!doctype html><meta charset="utf-8"><style>html,body{margin:0;background:#f4f4f2}body{zoom:.8;-moz-transform:scale(.8);-moz-transform-origin:0 0}</style>${preview.html}`
    }) : /*#__PURE__*/React.createElement("p", {
      className: "rl-hint rl-preview-empty"
    }, previewBusy ? "Getting the preview ready…" : "The preview isn't available right now.")), /*#__PURE__*/React.createElement("div", {
      className: "rl-recent"
    }, /*#__PURE__*/React.createElement("b", null, "Recently sent"), deliveries === null ? /*#__PURE__*/React.createElement("span", {
      className: "rl-hint"
    }, "Loading\u2026") : !deliveries.length ? /*#__PURE__*/React.createElement("span", {
      className: "rl-hint"
    }, lastSentDate ? `Last sent ${fmtWhen(lastSentDate, tz)}.` : "Nothing sent yet.") : deliveries.map(d => {
      const n = (d.recipients || []).length;
      const failed = d.status === "failed";
      return /*#__PURE__*/React.createElement("span", {
        key: d.id,
        className: "rl-recent-line" + (failed ? " bad" : ""),
        title: failed && d.error ? d.error : undefined
      }, fmtWhen(d.sentAt, tz), " \xB7 ", failed ? "didn't go out" : d.status === "test" ? "test sent to you" : `delivered to ${people(n)}`);
    }), onSeeAll ? /*#__PURE__*/React.createElement("a", {
      className: "rl-link",
      onClick: onSeeAll
    }, "See all sent emails") : /*#__PURE__*/React.createElement(Link, {
      className: "rl-link",
      to: "/Reports?tab=sent"
    }, "See all sent emails")));
  }
  render() {
    const {
      report,
      onClose
    } = this.props;
    const {
      busy,
      note,
      noteKind
    } = this.state;
    return /*#__PURE__*/React.createElement("div", {
      className: "rl-sheet-wrap"
    }, /*#__PURE__*/React.createElement("div", {
      className: "rl-sheet-backdrop",
      onClick: onClose
    }), /*#__PURE__*/React.createElement("aside", {
      className: "rl-sheet",
      role: "dialog",
      "aria-label": `Email ${report.name}`
    }, /*#__PURE__*/React.createElement("div", {
      className: "rl-sheet-head"
    }, /*#__PURE__*/React.createElement("div", {
      className: "rl-sheet-title"
    }, /*#__PURE__*/React.createElement("span", {
      className: "t"
    }, "Email this report"), /*#__PURE__*/React.createElement("span", {
      className: "s"
    }, report.name, " \xB7 numbers are fresh in every email")), /*#__PURE__*/React.createElement("span", {
      className: "rl-sheet-actions"
    }, /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
      disabled: busy,
      onClick: this.sendTest,
      title: "Saves your changes, then emails a copy to you only"
    }, "Send a test to me"), /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      disabled: busy,
      onClick: () => this.save()
    }, busy ? "Saving…" : "Save"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "rl-sheet-x",
      onClick: onClose,
      title: "Close",
      "aria-label": "Close"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "close")))), note && /*#__PURE__*/React.createElement("div", {
      className: "rl-sheet-note" + (noteKind === "error" ? " bad" : "")
    }, note), /*#__PURE__*/React.createElement("div", {
      className: "rl-sheet-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "rl-sheet-form"
    }, this.renderWhen(), this.renderCovers(), this.renderWho(), this.renderSending()), this.renderSide())));
  }
}
export function parseSchedule(json) {
  try {
    const s = JSON.parse(json || "null");
    return s && s.cadence ? s : null;
  } catch (e) {
    return null;
  }
}